import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  login: (email, password, companySlug = null) => 
    api.post('/auth/login', { email, password, company_slug: companySlug }),
  companyLookup: (slug) => api.post('/auth/company-lookup', { slug }),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => 
    api.put('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  resetPassword: (userId, newPassword) => 
    api.put('/auth/reset-password', { user_id: userId, new_password: newPassword }),
  register: (userData) => api.post('/auth/register', userData),
  updateProfile: (data) => api.put('/auth/profile', data),
  updateSuperAdminCredentials: (currentPassword, email = null, newPassword = null) =>
    api.put('/super-admin/credentials', { current_password: currentPassword, email, new_password: newPassword }),
};

// Company APIs (Super Admin)
export const companyApi = {
  list: () => api.get('/companies'),
  get: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  toggleStatus: (id) => api.put(`/companies/${id}/toggle-status`),
  delete: (id) => api.delete(`/companies/${id}`),
  update: (id, data) => api.put(`/companies/${id}`, data),
  getUsers: (companyId) => api.get(`/companies/${companyId}/users`),
  resetUserPassword: (userId, newPassword) => api.post(`/users/${userId}/reset-password`, { password: newPassword }),
  createUser: (companyId, userData) => api.post(`/companies/${companyId}/users`, userData),
};

// User APIs
export const userApi = {
  list: () => api.get('/users'),
  listAssignable: () => api.get('/users/assignable'),
  get: (id) => api.get(`/users/${id}`),
  delete: (id) => api.delete(`/users/${id}`),
  updatePermissions: (id, data) => api.put(`/users/${id}/permissions`, data),
  vincularFuncionario: (userId, humanId) => api.put('/users/vincular-funcionario', { user_id: userId, human_id: humanId }),
};

// Department APIs
export const departmentApi = {
  list: () => api.get('/departments'),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
  listSectors: (deptId) => api.get(`/departments/${deptId}/sectors`),
  reorder: (departmentIds) => api.put('/departments/reorder', { department_ids: departmentIds }),
};

// Sector APIs (Subgrupos de Departamentos)
export const sectorApi = {
  list: (departmentId = null) => api.get('/sectors', { params: { department_id: departmentId } }),
  listForCalendar: (year = null) => api.get('/sectors/calendar', { params: { year } }),
  create: (data) => api.post('/sectors', data),
  update: (id, data) => api.put(`/sectors/${id}`, data),
  delete: (id) => api.delete(`/sectors/${id}`),
  cleanupOrphans: () => api.delete('/sectors/cleanup-orphans'),
};

// Project APIs
export const projectApi = {
  list: (departmentId = null) => api.get('/projects', { params: { department_id: departmentId } }),
  listWithStats: (departmentId = null) => api.get('/projects/with-stats', { params: { department_id: departmentId } }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  updatePermissions: (id, allowedUsers) => api.put(`/projects/${id}/permissions`, { allowed_users: allowedUsers }),
};

// Column APIs
export const columnApi = {
  list: (projectId) => api.get(`/projects/${projectId}/columns`),
  create: (projectId, data) => api.post(`/projects/${projectId}/columns`, data),
  update: (columnId, data) => api.put(`/columns/${columnId}`, data),
  delete: (columnId) => api.delete(`/columns/${columnId}`),
};

// Task APIs
export const taskApi = {
  list: (projectId) => api.get(`/projects/${projectId}/tasks`),
  get: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  move: (id, columnId, order) => api.put(`/tasks/${id}/move`, { column_id: columnId, order }),
  addComment: (id, text) => api.post(`/tasks/${id}/comments`, { text }),
  delete: (id) => api.delete(`/tasks/${id}`),
  // Personal tasks (without project)
  listPersonal: () => api.get('/tasks/personal'),
  createPersonal: (data) => api.post('/tasks/personal', data),
};

// Activity Log APIs
export const activityApi = {
  list: (taskId = null, limit = 50) => 
    api.get('/activity-logs', { params: { task_id: taskId, limit } }),
  listMyActivities: (limit = 20) =>
    api.get('/activity-logs', { params: { my_activities: true, limit } }),
  listRelevant: (limit = 20) => api.get('/activity-logs/relevant', { params: { limit } }),
};

// Notification APIs
export const notificationApi = {
  list: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Moon/Astrology APIs
export const moonApi = {
  getCurrent: () => api.get('/moon/current'),
  getByDate: (date) => api.get(`/moon/date/${date}`),
};

export const zodiacApi = {
  getCurrent: () => api.get('/zodiac/current'),
  getByDate: (date) => api.get(`/zodiac/date/${date}`),
};

// Holidays/Feriados APIs (Manaus/AM)
export const holidaysApi = {
  getCurrent: () => api.get('/holidays/current'),
  getByDate: (date) => api.get(`/holidays/date/${date}`),
  getByMonth: (year, month) => api.get(`/holidays/month/${year}/${month}`),
};

// Flags API
export const flagsApi = {
  getFlaggedItems: () => api.get('/dashboard/flagged-items'),
  updateProjectFlag: (projectId, flag) => api.patch(`/projects/${projectId}/flag`, { flag }),
  updateTaskFlag: (taskId, flag) => api.patch(`/tasks/${taskId}/flag`, { flag }),
};

// Super Admin APIs
export const superAdminApi = {
  getSystemArchitecture: () => api.get('/super-admin/system-architecture'),
};

// Calendar APIs
export const calendarApi = {
  getTasks: (startDate, endDate) => 
    api.get('/calendar/tasks', { params: { start_date: startDate, end_date: endDate } }),
};

// Stats APIs
export const statsApi = {
  getDashboard: () => api.get('/stats/dashboard'),
  getMyTasks: (date = null) => api.get('/dashboard/my-tasks', { params: { date } }),
  getDailyControlSummary: (mes, ano) => api.get('/stats/daily-control-summary', { params: { mes, ano } }),
  getTeamActivity: () => api.get('/dashboard/team-activity'),
  getChartsData: (humanId = null) => api.get('/dashboard/charts-data', { 
    params: humanId && humanId !== 'todos' ? { human_id: humanId } : {} 
  }),
  getCltResumoMes: (mes = null, ano = null) => api.get('/dashboard/clt-resumo-mes', { 
    params: { mes, ano } 
  }),
};

// Reports APIs
export const reportsApi = {
  getMonthlyView: (year, month) => api.get(`/reports/monthly-view/${year}/${month}`),
  getWeeklySummary: (startDate, endDate) => api.get('/reports/weekly-summary', { params: { start_date: startDate, end_date: endDate } }),
  getAuditReport: (startDate, endDate) => api.get('/reports/audit', { params: { start_date: startDate, end_date: endDate } }),
  getFuncionarioReport: (funcionarioId, mes, ano) => api.get(`/reports/funcionario/${funcionarioId}`, { params: { mes, ano } }),
};

// Seed API
export const seedApi = {
  createSuperAdmin: () => api.post('/seed/super-admin'),
};

// Daily Control APIs
export const dailyControlApi = {
  get: (date) => api.get(`/daily-control/${date}`),
  save: (data) => api.post('/daily-control', data),
  delete: (date) => api.delete(`/daily-control/${date}`),
  updateAudit: (controlId, notes) => api.put(`/daily-control/${controlId}/audit`, { audit_notes: notes }),
  getReport: (date) => api.get(`/daily-control/${date}/report`),
  getRange: (startDate, endDate) => api.get('/daily-controls/range', { params: { start_date: startDate, end_date: endDate } }),
  // Records (apontamentos do dia)
  listRecords: (date) => api.get(`/daily-control/${date}/records`),
  addRecord: (date, data) => api.post(`/daily-control/${date}/records`, data),
  updateRecord: (date, recordId, data) => api.put(`/daily-control/${date}/records/${recordId}`, data),
  removeRecord: (date, recordId) => api.delete(`/daily-control/${date}/records/${recordId}`),
  initializeDay: (date) => api.post(`/daily-control/${date}/initialize`),
  // Meals (refeições)
  getMeals: (date) => api.get(`/daily-control/${date}/meals`),
  saveMeals: (date, data) => api.put(`/daily-control/${date}/meals`, data),
  // Vehicle Notes (notas do Contele e gastos)
  getVehicleNotes: (date) => api.get(`/daily-control/${date}/vehicle-notes`),
  saveVehicleNotes: (date, data) => api.post(`/daily-control/${date}/vehicle-notes`, data),
  // Transport Voucher (vale transporte)
  getTransportVoucher: (date) => api.get(`/daily-control/${date}/transport-voucher`),
  saveTransportVoucher: (date, data) => api.post(`/daily-control/${date}/transport-voucher`, data),
  // Workers (terceiros no dia) - deprecated, use records
  listWorkers: (date) => api.get(`/daily-control/${date}/records`),
  addWorker: (date, data) => api.post(`/daily-control/${date}/records`, data),
  removeWorker: (date, workerId) => api.delete(`/daily-control/${date}/records/${workerId}`),
  // Wastes (desperdícios)
  listWastes: (date) => api.get(`/daily-control/${date}/wastes`),
  addWaste: (date, data) => api.post(`/daily-control/${date}/wastes`, data),
  removeWaste: (date, wasteId) => api.delete(`/daily-control/${date}/wastes/${wasteId}`),
  // Termos Stats (ranking de registros manuais)
  getTermosStats: (month, year) => api.get('/daily-control/termos-stats', { params: { month, year } }),
  // Reembolsos Exceções
  getReembolsosExcecoes: (date) => api.get(`/daily-control/${date}/reembolsos-excecoes`),
  saveReembolsosExcecoes: (date, data) => api.post(`/daily-control/${date}/reembolsos-excecoes`, data),
  getVencimentosReembolsosExcecoes: (params = {}) => api.get('/reembolsos-excecoes/vencimentos', { params }),
};

// Humanas (Pessoas) APIs
export const humanApi = {
  list: (params = {}) => api.get('/humans', { params }),
  get: (id) => api.get(`/humans/${id}`),
  create: (data) => api.post('/humans', data),
  update: (id, data) => api.put(`/humans/${id}`, data),
  delete: (id) => api.delete(`/humans/${id}`),
  // Ocorrências
  listOccurrences: (humanId) => api.get(`/humans/${humanId}/occurrences`),
  addOccurrence: (humanId, data) => api.post(`/humans/${humanId}/occurrences`, data),
  // Histórico de Salário/Cargo (CLT)
  getSalarioHistorico: (humanId) => api.get(`/humans/${humanId}/salario-historico`),
  getSalarioVigente: (humanId, competencia = null) => api.get(`/humans/${humanId}/salario-vigente`, { params: { competencia } }),
  addSalarioVigencia: (humanId, data) => api.post(`/humans/${humanId}/salario-vigencia`, data),
};

// Feedback/Suggestions APIs
export const feedbackApi = {
  send: (message, type = 'sugestao') => api.post('/feedback', { message, type }),
  list: () => api.get('/feedback'),
  update: (id, data) => api.put(`/feedback/${id}`, data),
};

// Pendências Operacionais APIs
export const pendenciasApi = {
  list: (params = {}) => api.get('/pendencias', { params }),
  getResumo: (mes = null, ano = null) => api.get('/pendencias/resumo', { params: { mes, ano } }),
  getDia: (data) => api.get(`/pendencias/dia/${data}`),
  create: (data) => api.post('/pendencias', data),
  update: (id, data) => api.put(`/pendencias/${id}`, data),
  delete: (id) => api.delete(`/pendencias/${id}`),
};

// Ordens de Serviço (OS) APIs
export const osApi = {
  list: (params = {}) => api.get('/os', { params }),
  get: (numero) => api.get(`/os/${numero}`),
  create: (data) => api.post('/os', data),
  update: (numero, data) => api.put(`/os/${numero}`, data),
  delete: (numero) => api.delete(`/os/${numero}`),
  executar: (numero, insumosAjustados = null) => api.post(`/os/${numero}/executar`, { insumos_ajustados: insumosAjustados }),
  reverterExecucao: (numero) => api.post(`/os/${numero}/reverter-execucao`),
};

// Performance/Folha Inteligente APIs
export const performanceApi = {
  getReport: (mes, ano) => api.get('/reports/performance', { params: { mes, ano } }),
  getFuncionario: (humanId, mes, ano) => api.get(`/reports/performance/funcionario/${humanId}`, { params: { mes, ano } }),
  getTermos: (mes, ano) => api.get('/reports/termos', { params: { mes, ano } }),
  exportTermosCsv: (mes, ano) => api.get('/reports/termos/export-csv', { params: { mes, ano }, responseType: 'blob' }),
  getJornadaClt: (humanId, mes, ano) => api.get(`/reports/jornada-clt/${humanId}`, { params: { mes, ano } }),
};

// Feriados APIs
export const feriadosApi = {
  list: (ano) => api.get('/feriados', { params: { ano } }),
  create: (data) => api.post('/feriados', data),
  update: (id, data) => api.put(`/feriados/${id}`, data),
  delete: (id) => api.delete(`/feriados/${id}`),
  seedDefault: () => api.post('/feriados/seed-default'),
};

// Abono APIs
export const abonoApi = {
  abonar: (recordId, data) => api.post(`/day-records/${recordId}/abonar`, data),
  abonarPorData: (data) => api.post(`/day-records/abonar-por-data`, data),
  remover: (recordId) => api.post(`/day-records/${recordId}/remover-abono`),
};

// Estoque APIs
export const estoqueApi = {
  // Produtos
  listarProdutos: (params = {}) => api.get('/estoque/produtos', { params }),
  obterProduto: (id) => api.get(`/estoque/produtos/${id}`),
  criarProduto: (data) => api.post('/estoque/produtos', data),
  atualizarProduto: (id, data) => api.put(`/estoque/produtos/${id}`, data),
  excluirProduto: (id) => api.delete(`/estoque/produtos/${id}`),
  
  // Locais
  listarLocais: (params = {}) => api.get('/estoque/locais', { params }),
  criarLocal: (data) => api.post('/estoque/locais', data),
  atualizarLocal: (id, data) => api.put(`/estoque/locais/${id}`, data),
  excluirLocal: (id) => api.delete(`/estoque/locais/${id}`),
  
  // Movimentações
  listarMovimentacoes: (params = {}) => api.get('/estoque/movimentacoes', { params }),
  criarMovimentacao: (data) => api.post('/estoque/movimentacoes', data),
  
  // Saldos (admin only)
  listarSaldos: (params = {}) => api.get('/estoque/saldos', { params }),
  resumoSaldos: () => api.get('/estoque/saldos/resumo'),
  
  // Upload foto
  uploadFoto: (fotoBase64) => api.post('/estoque/upload-foto', { foto: fotoBase64 }),
  uploadFotoFile: (formData) => api.post('/estoque/upload-foto', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // NF-e
  parseNfe: (xml) => api.post('/estoque/parse-nfe', { xml }),
  parseNfeFile: (formData) => api.post('/estoque/parse-nfe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  parseNfePdf: (formData) => api.post('/estoque/parse-nfe-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  importarNfe: (data) => api.post('/estoque/importar-nfe', data),
  
  // Categorias
  listarCategorias: () => api.get('/estoque/categorias'),
  
  // Verificar entrada similar (para aviso de duplicidade)
  verificarEntradaSimilar: (produto_id, quantidade, dias_atras = 30) => 
    api.get('/estoque/verificar-entrada-similar', { params: { produto_id, quantidade, dias_atras } }),
  
  // Inventário / Reconciliação
  listarInventarios: (params = {}) => api.get('/estoque/inventarios', { params }),
  obterInventario: (id) => api.get(`/estoque/inventarios/${id}`),
  criarInventario: (data) => api.post('/estoque/inventarios', data),
  aplicarAjustes: (inventarioId) => api.post(`/estoque/inventarios/${inventarioId}/aplicar-ajustes`),
  saldosPorLocal: (localId, dataReferencia = null) => 
    api.get(`/estoque/saldos-por-local/${localId}`, { params: { data_referencia: dataReferencia } }),
  statsInventarios: () => api.get('/estoque/inventarios/stats/resumo'),
};

// Banco de Ideias APIs
export const ideiasApi = {
  listar: (params = {}) => api.get('/ideias', { params }),
  obter: (id) => api.get(`/ideias/${id}`),
  criar: (data) => api.post('/ideias', data),
  atualizar: (id, data) => api.put(`/ideias/${id}`, data),
  adicionarTratamento: (id, data) => api.post(`/ideias/${id}/tratamento`, data),
  uploadAnexo: (arquivoBase64) => api.post('/ideias/upload-anexo', { arquivo: arquivoBase64 }),
  resumo: () => api.get('/ideias/stats/resumo'),
  dashboard: () => api.get('/ideias/dashboard'),
};

export const cicloMelhoriaApi = {
  list: () => api.get('/ciclos-melhoria'),
  get: (id) => api.get(`/ciclos-melhoria/${id}`),
  create: (data) => api.post('/ciclos-melhoria', data),
  update: (id, data) => api.put(`/ciclos-melhoria/${id}`, data),
  delete: (id) => api.delete(`/ciclos-melhoria/${id}`),
  addProblema: (cicloId, data) => api.post(`/ciclos-melhoria/${cicloId}/problemas`, data),
  updateProblema: (cicloId, problemaId, data) => api.put(`/ciclos-melhoria/${cicloId}/problemas/${problemaId}`, data),
  removeProblema: (cicloId, problemaId) => api.delete(`/ciclos-melhoria/${cicloId}/problemas/${problemaId}`),
  getResumoAtual: () => api.get('/ciclos-melhoria/resumo/atual'),
};

export const fornecedorRefeicaoApi = {
  list: (ativo = true) => api.get(`/fornecedores-refeicao${ativo !== null ? `?ativo=${ativo}` : ''}`),
  create: (data) => api.post('/fornecedores-refeicao', data),
  update: (id, data) => api.put(`/fornecedores-refeicao/${id}`, data),
  delete: (id) => api.delete(`/fornecedores-refeicao/${id}`),
};

export const refeicoesDiaApi = {
  get: (date) => api.get(`/daily-control/${date}/refeicoes-fornecedor`),
  save: (date, data) => api.post(`/daily-control/${date}/refeicoes-fornecedor`, data),
  setVencimento: (date, data) => api.post(`/daily-control/${date}/refeicoes-vencimento`, data),
};

export const vencimentosRefeicaoApi = {
  listar: (params = {}) => api.get('/refeicoes/vencimentos', { params }),
  quitar: (data) => api.post('/refeicoes/vencimentos/quitar', data),
  estornar: (data) => api.post('/refeicoes/vencimentos/estornar', data),
};

export const vencimentosTerceirosApi = {
  listar: (params = {}) => api.get('/terceiros/vencimentos', { params }),
  quitar: (data) => api.post('/terceiros/vencimentos/quitar', data),
  estornar: (data) => api.post('/terceiros/vencimentos/estornar', data),
};

export const vtApi = {
  getSaldoMensal: (ano, mes) => api.get(`/vt/saldo/${ano}/${mes}`),
  getFuncionarioDetalhado: (funcionarioId, ano, mes) => api.get(`/vt/funcionario/${funcionarioId}/${ano}/${mes}`),
  getMeuExtrato: (ano, mes) => api.get(`/vt/meu-extrato/${ano}/${mes}`),
  updateDia: (recordId, data) => api.put(`/vt/dia/${recordId}`, data),
  getConfig: () => api.get('/vt/config'),
  updateConfig: (data) => api.put('/vt/config', data),
};

export const tiposAtividadeApi = {
  list: (ativo = true) => api.get(`/tipos-atividade${ativo !== null ? `?ativo=${ativo}` : ''}`),
  create: (data) => api.post('/tipos-atividade', data),
  update: (id, data) => api.put(`/tipos-atividade/${id}`, data),
  delete: (id) => api.delete(`/tipos-atividade/${id}`),
};

export const veiculosApi = {
  list: (ativo = true) => api.get(`/veiculos${ativo !== null ? `?ativo=${ativo}` : ''}`),
  create: (data) => api.post('/veiculos', data),
  update: (id, data) => api.put(`/veiculos/${id}`, data),
  delete: (id) => api.delete(`/veiculos/${id}`),
};

export default api;
